<template>
  <div class="service-tickets">
    <div class="page-header">
      <p class="title">Service Tickets</p>
      <p class="subtitle">
        Request a service below. Each ticket has a fixed fee that compensates
        staff time for reviewing, updating records, and coordinating with you.
      </p>
    </div>

    <div class="section-title">Available Services</div>
    <div class="service-grid">
      <div
        class="service-card"
        v-for="svc in services"
        :key="svc.type"
        @click="openService(svc)">
        <div class="service-card-header">
          <i :class="[svc.icon, 'service-icon']"></i>
          <div class="service-price">
            <template v-if="svc.price > 0">
              <span class="currency">USD</span>
              <span class="amount">{{ svc.price }}</span>
            </template>
            <span v-else class="amount free">Free</span>
          </div>
        </div>
        <p class="service-name">{{ svc.name }}</p>
        <p class="service-desc">{{ svc.description }}</p>
        <div class="service-cta">Request &rarr;</div>
      </div>
    </div>

    <div class="section-title" style="margin-top: 40px;">My Tickets</div>
    <div v-if="loading" class="empty-state">Loading...</div>
    <div v-else-if="!tickets.length" class="empty-state">
      You haven't submitted any service tickets yet.
    </div>
    <el-table v-else :data="tickets" class="tickets-table" @row-click="openTicket">
      <el-table-column label="Service" min-width="180">
        <template slot-scope="scope">
          {{ typeLabel(scope.row.ticket_type) }}
        </template>
      </el-table-column>
      <el-table-column label="Amount" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.amount_usd > 0">USD {{ scope.row.amount_usd }}</span>
          <span v-else class="free-label">Free</span>
        </template>
      </el-table-column>
      <el-table-column label="Payment" width="150">
        <template slot-scope="scope">
          <el-tag :type="paymentTag(scope.row.payment_status)" size="small">
            {{ paymentLabel(scope.row.payment_status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Ticket Status" width="160">
        <template slot-scope="scope">
          <el-tag :type="statusTag(scope.row.status)" size="small">
            {{ statusLabel(scope.row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Submitted" width="160">
        <template slot-scope="scope">{{ formatTime(scope.row.create_time) }}</template>
      </el-table-column>
      <el-table-column label="" width="70">
        <template slot-scope="scope">
          <el-button type="text" @click.stop="openTicket(scope.row)">View</el-button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import { getMyServiceTickets } from '../../api/eae'
import { getUserId } from '../../utils/store'

const SERVICES = [
  {
    type: 'name_correction',
    name: 'Name Change',
    icon: 'el-icon-edit-outline',
    price: 40,
    description:
      'Correct your registered name after submission. Covers staff review, system update, and confirmation.',
    route: 'newNameCorrection',
  },
  {
    type: 'school_change',
    name: 'School Change',
    icon: 'el-icon-school',
    price: 40,
    description:
      'Change the school linked to your registration. Covers staff verification, record updates, and confirmation.',
    route: 'newSchoolChange',
  },
  {
    type: 'results_dispute',
    name: 'Results Dispute',
    icon: 'el-icon-document-checked',
    price: 0,
    description:
      'Request a review of a result you believe is incorrect (score, award, or rubric application). No fee — submit details and our staff will respond.',
    route: 'newResultsDispute',
  },
]

const TYPE_LABELS = {
  name_correction: 'Name Correction',
  school_change: 'School Change',
  results_dispute: 'Results Dispute',
}

const STATUS_LABELS = {
  pending_review: 'Pending Review',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

const STATUS_TAG = {
  pending_review: 'warning',
  in_progress: '',
  resolved: 'success',
  rejected: 'danger',
  cancelled: 'info',
}

const PAYMENT_LABELS = {
  pending_payment: 'Pending Payment',
  paid: 'Paid',
  free: 'Free',
}

const PAYMENT_TAG = {
  pending_payment: 'warning',
  paid: 'success',
  free: 'info',
}

export default {
  name: 'ServiceTicketsIndex',
  data() {
    return {
      services: SERVICES,
      tickets: [],
      loading: false,
    }
  },
  mounted() {
    this.fetchTickets()
  },
  methods: {
    openService(svc) {
      this.$router.push({ path: '/home/serviceTickets/' + svc.route })
    },
    openTicket(row) {
      this.$router.push({ path: '/home/serviceTickets/' + row._id.$id })
    },
    fetchTickets() {
      const student_id = getUserId()
      if (!student_id) {
        this.tickets = []
        return
      }
      this.loading = true
      getMyServiceTickets(student_id)
        .then((res) => {
          this.tickets = (res.data && res.data.data) || []
        })
        .finally(() => {
          this.loading = false
        })
    },
    typeLabel(t) {
      return TYPE_LABELS[t] || t
    },
    statusLabel(s) {
      return STATUS_LABELS[s] || s
    },
    statusTag(s) {
      return STATUS_TAG[s] !== undefined ? STATUS_TAG[s] : ''
    },
    paymentLabel(s) {
      return PAYMENT_LABELS[s] || s || '—'
    },
    paymentTag(s) {
      return PAYMENT_TAG[s] !== undefined ? PAYMENT_TAG[s] : ''
    },
    formatTime(ts) {
      if (!ts) return ''
      const d = new Date(ts * 1000)
      const pad = (n) => (n < 10 ? '0' + n : n)
      return (
        d.getFullYear() +
        '-' +
        pad(d.getMonth() + 1) +
        '-' +
        pad(d.getDate()) +
        ' ' +
        pad(d.getHours()) +
        ':' +
        pad(d.getMinutes())
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.service-tickets {
  padding: 24px 32px;
  max-width: 1100px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 28px;

  .title {
    font-size: 24px;
    font-weight: bold;
    color: #ff8e3b;
    margin-bottom: 8px;
  }

  .subtitle {
    color: #666;
    font-size: 14px;
    line-height: 1.6;
    max-width: 780px;
  }
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 14px;
}

.service-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

.service-card {
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 18px 20px;
  background: #fff;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  display: flex;
  flex-direction: column;
  min-height: 180px;

  &:hover {
    box-shadow: 0 6px 20px rgba(255, 142, 59, 0.15);
    transform: translateY(-2px);
    border-color: #ffd7b3;
  }
}

.service-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.service-icon {
  font-size: 28px;
  color: #ff8e3b;
}

.service-price {
  .currency {
    font-size: 12px;
    color: #999;
    margin-right: 4px;
  }

  .amount {
    font-size: 22px;
    font-weight: bold;
    color: #ff8e3b;

    &.free {
      font-size: 18px;
      color: #2e7d32;
    }
  }
}

.free-label {
  color: #2e7d32;
  font-weight: 500;
}

.service-name {
  font-size: 16px;
  font-weight: bold;
  color: #333;
  margin-bottom: 6px;
}

.service-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
  flex: 1;
}

.service-cta {
  margin-top: 12px;
  color: #ff8e3b;
  font-size: 13px;
  font-weight: 500;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #999;
  font-size: 14px;
  background: #fafafa;
  border-radius: 8px;
}

.tickets-table {
  cursor: pointer;
}
</style>
